import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Shared KB Markdown renderer (§38 rich media). Renders article Markdown and
 * resolves inline-image markers (`kbimg:<name>`) to fresh SIGNED Blob URLs via a
 * caller-supplied resolver path — so images stay private + tenant-scoped and the
 * body never stores an expiring URL. Also assigns stable heading ids (h2/h3) for
 * the table of contents. Used by the editor preview, the staff read view, and
 * the customer portal read view (each passing its own resolver).
 */
import { useQuery } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
export function slugify(s) {
    return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
}
export function headings(md) {
    const out = [];
    for (const line of (md || '').split('\n')) {
        const m = /^(#{2,3})\s+(.+?)\s*$/.exec(line);
        if (m && m[1] && m[2])
            out.push({ level: m[1].length === 2 ? 'h2' : 'h3', text: m[2], id: slugify(m[2]) });
    }
    return out;
}
function nodeText(children) {
    if (typeof children === 'string')
        return children;
    if (Array.isArray(children))
        return children.map(nodeText).join('');
    return '';
}
// Resolve a `kbimg:<name>` marker to a signed URL via the given resolver path.
function KbImage({ name, alt, resolvePath }) {
    const q = useQuery({
        queryKey: ['kb-img', resolvePath(name)],
        queryFn: () => api.get(resolvePath(name)),
        staleTime: 50 * 60 * 1000, // signed URL lives ~60 min; refetch a bit before
        retry: false,
    });
    if (q.isError)
        return _jsx("span", { className: "text-xs italic text-slate-400", children: "[image unavailable]" });
    if (!q.data)
        return _jsx("span", { className: "my-2 inline-block h-32 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800" });
    return _jsx("img", { src: q.data.url, alt: alt ?? '', className: "my-2 max-w-full rounded border border-slate-200 dark:border-slate-800" });
}
// Resolve a `kbfile:<name>` link to a signed download URL (lazily — only when
// the reader clicks, so we don't mint a URL per attachment on every render).
function KbFile({ name, label, resolvePath }) {
    const q = useQuery({
        queryKey: ['kb-file', resolvePath(name)],
        queryFn: () => api.get(resolvePath(name)),
        enabled: false, // fetch on demand (click)
        staleTime: 50 * 60 * 1000,
        retry: false,
    });
    const onClick = async () => {
        const res = q.data ?? (await q.refetch()).data;
        if (res?.url)
            window.open(res.url, '_blank', 'noopener,noreferrer');
    };
    return (_jsxs("button", { type: "button", onClick: onClick, disabled: q.isFetching, className: "my-1 inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1 text-sm font-medium text-brand-700 hover:bg-slate-100 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-brand-300 dark:hover:bg-slate-700", children: [_jsx(Download, { size: 14 }), " ", label || name] }));
}
export function KbMarkdown({ body, resolvePath, resolveFilePath, }) {
    return (_jsx(ReactMarkdown, { components: {
            h2: ({ children }) => _jsx("h2", { id: slugify(nodeText(children)), children: children }),
            h3: ({ children }) => _jsx("h3", { id: slugify(nodeText(children)), children: children }),
            img: ({ src, alt }) => {
                const s = String(src ?? '');
                return s.startsWith('kbimg:') ? (_jsx(KbImage, { name: s.slice(6), alt: alt, resolvePath: resolvePath })) : (_jsx("img", { src: s, alt: alt ?? '', className: "my-2 max-w-full rounded" }));
            },
            a: ({ href, children }) => {
                const h = String(href ?? '');
                if (h.startsWith('kbfile:') && resolveFilePath) {
                    return _jsx(KbFile, { name: h.slice(7), label: nodeText(children), resolvePath: resolveFilePath });
                }
                // In-app links (e.g. a cross-link to another KB article) navigate via
                // the router instead of a full page reload.
                if (h.startsWith('/'))
                    return _jsx(Link, { to: h, children: children });
                if (h.startsWith('#'))
                    return _jsx("a", { href: h, children: children });
                return (_jsx("a", { href: h, target: "_blank", rel: "noopener noreferrer", children: children }));
            },
        }, children: body }));
}
// Resolver-path helpers so callers don't hand-build query strings.
export const staffImageResolver = (name) => `/kb/image-url?name=${encodeURIComponent(name)}`;
export const portalImageResolver = (slug) => (name) => `/portal/kb/articles/${slug}/image-url?name=${encodeURIComponent(name)}`;
export const staffFileResolver = (name) => `/kb/file-url?name=${encodeURIComponent(name)}`;
export const portalFileResolver = (slug) => (name) => `/portal/kb/articles/${slug}/file-url?name=${encodeURIComponent(name)}`;
